import { Input, Select } from 'antd';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import { useGetMappedStatusEvent } from '@api/emp/queryHooks';
import { AutoCompleteInput } from '@components/common/AutoCompleteInput';
import { buildOptionsWithTag } from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { createFiltersGroup } from '@components/mapping-panel/utils/createFiltersUtils';
import { mappedEventDimensionType } from '@src/common/constant/enum';
import { EStringOp } from '@src/emp-3.0/types';
import { isEmptyOrNotEmptyOp, getOperatorOptions, validateString } from '@src/emp-3.0/utils';

import styles from './index.less';
import { EInputType, IProps } from './typed';
import { useTrackMappedEventSensorStore } from '@components/mapping-panel/store/sensorStore';

const KeyValueBuilder: React.FC<IProps> = (props: IProps) => {
  const { id, disabled, eventName, data, onChange, active, onActiveChange } = props;

  const { setTagKeyOptionList } = useTrackMappedEventSensorStore((state) => state);

  const { operatorOptions } = getOperatorOptions();

  const isTagValueNonContainsOperator =
    data.tagValue.operator !== EStringOp.CONTAINS &&
    data.tagValue.operator !== EStringOp.NOT_CONTAINS;

  const [tagKeyOptions, setTagKeyOptions] = useState<any[]>([]);
  const [tagValueOptions, setTagValueOptions] = useState<any[]>([]);
  const [tagValueSearchText, setTagValueSearchText] = useState<string>('');
  const [tagKeySearchText, setTagKeySearchText] = useState<string>('');

  const { data: mappedTagKeyData } = useGetMappedStatusEvent('tag_key');
  const { data: mappedTagValueData } = useGetMappedStatusEvent('tag_value');
  const { data: tagKeyData, isFetching: isKeyLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.merged_event_tag_key],
      filters: isTagValueNonContainsOperator
        ? createFiltersGroup(eventName, undefined, data.tagValue.value)
        : createFiltersGroup(eventName),
    },
    500,
  );
  const { data: tagValueData, isFetching: isTagValueLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.merged_event_tag_value],
      filters: createFiltersGroup(eventName, data.tagKey),
    },
    500,
  );

  useEffect(() => {
    if (eventName) {
      setTagKeyOptionList(eventName, tagKeyOptions);
    }
  }, [eventName, tagKeyOptions]);

  useEffect(() => {
    const options = buildOptionsWithTag(
      tagKeyData || [],
      mappedEventDimensionType.merged_event_tag_key,
      tagKeySearchText,
      mappedTagKeyData?.data,
    );
    setTagKeyOptions(options);
  }, [tagKeyData, mappedTagKeyData?.data, tagKeySearchText]);

  useEffect(() => {
    const options = buildOptionsWithTag(
      tagValueData || [],
      mappedEventDimensionType.merged_event_tag_value,
      tagValueSearchText,
      mappedTagValueData?.data,
    );
    setTagValueOptions(options);
  }, [tagValueData, mappedTagValueData?.data, tagValueSearchText]);

  const { failed: isIllegalityTagKey } = validateString(data?.tagKey);

  const { failed: isIllegalityTagValue } = validateString(data?.tagValue?.value, {
    matchPattern: data?.tagValue?.operator === EStringOp.MATCH_PATTERN,
    validateLegality: false,
    validateSecure: true,
  });

  const handleTagKeyChange = (value) => {
    setTagKeySearchText(value);
    onChange?.(EInputType.KEY, value);
  };

  const handleTagValueChange = (value) => {
    setTagValueSearchText(value);
    onChange?.(EInputType.VALUE, value);
  };

  return (
    <div className={styles.rootContainer}>
      <div className={styles.itemContainer}>
        <div className={active?.key ? styles.titleFocus : styles.title}>Tag Key</div>
        <div className={styles.operator}>=</div>
        <AutoCompleteInput
          data-test-id={`tag_key_${id}`}
          disabled={disabled}
          mode={'autoComplete'}
          active={active?.key}
          validateStatus={isIllegalityTagKey ? 'error' : undefined}
          value={data?.tagKey}
          style={{ display: 'flex', flex: '1' }}
          options={tagKeyOptions}
          onChange={handleTagKeyChange}
          onFocus={() => onActiveChange(EInputType.KEY, true)}
          onBlur={() => onActiveChange(EInputType.KEY, false)}
          loading={isKeyLoading}
          onSearch={(text) => {
            setTagKeySearchText(text);
          }}
        />
      </div>
      <div className={styles.itemContainer}>
        <div className={active?.value ? styles.titleFocus : styles.title}>Tag Value</div>
        <div
          className={classnames(styles.selectWrapper, {
            [styles.selectActive]: active?.value,
          })}
        >
          <Select
            disabled={disabled}
            style={{ margin: '8px' }}
            options={operatorOptions}
            showArrow={false}
            value={data?.tagValue?.operator}
            onChange={(value) => {
              onChange?.(EInputType.OPERATOR, value);
            }}
            onFocus={(e) => {
              onActiveChange(EInputType.VALUE, true);
              e.stopPropagation();
            }}
            onBlur={() => onActiveChange(EInputType.VALUE, false)}
            onClick={(e) => e.stopPropagation()}
            dropdownStyle={{
              width: 'fit-content',
              minWidth: '130px',
              maxWidth: 'fit-content',
            }}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </div>
        {isEmptyOrNotEmptyOp(data?.tagValue?.operator) ? (
          <Input data-test-id={`tag_value_${id}`} disabled />
        ) : (
          <AutoCompleteInput
            data-test-id={`tag_value_${id}`}
            disabled={disabled}
            active={active?.value}
            validateStatus={isIllegalityTagValue ? 'error' : undefined}
            value={data?.tagValue?.value}
            style={{ display: 'flex', flex: '1' }}
            options={tagValueOptions}
            onChange={handleTagValueChange}
            onFocus={() => onActiveChange(EInputType.VALUE, true)}
            onBlur={() => onActiveChange(EInputType.VALUE, false)}
            loading={isTagValueLoading}
            onSearch={(text) => {
              setTagValueSearchText(text);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default KeyValueBuilder;
